import { useTranslation } from 'react-i18next';
import { Input, Label, Textarea } from '@/components/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type {
  IqServiceBookingType,
  IqServiceFlowType,
  IqServiceInventoryItem,
  IqServiceInventoryStatus,
} from '@/types/iq-service-inventory';
import {
  IQ_SERVICE_CREATE_STATUS_OPTIONS,
  IQ_SERVICE_KEY_PATTERN,
  iqServiceEditStatusOptions,
} from './iq-service-inventory-status';

const NONE = '__none__';
const MAX_DISPLAY_NAME = 100;
const MAX_DESCRIPTION = 500;
const MAX_EMAIL_REF = 256;

export interface IqServiceUpsertValues {
  iqServiceKey: string;
  flowType: IqServiceFlowType | null;
  bookingType: IqServiceBookingType | null;
  displayName: string;
  description: string;
  internetqEmailRef: string;
  status: IqServiceInventoryStatus;
}

/** Blank create-form state. */
export function emptyUpsertValues(): IqServiceUpsertValues {
  return {
    iqServiceKey: '',
    flowType: null,
    bookingType: null,
    displayName: '',
    description: '',
    internetqEmailRef: '',
    status: 'active',
  };
}

/** Prefill state from an existing record (carries the optimistic-lock source via `status`/`updatedAt` upstream). */
export function upsertValuesFromItem(item: IqServiceInventoryItem): IqServiceUpsertValues {
  return {
    iqServiceKey: item.iqServiceKey,
    flowType: item.flowType ?? null,
    bookingType: item.bookingType ?? null,
    displayName: item.displayName ?? '',
    description: item.description ?? '',
    internetqEmailRef: item.internetqEmailRef ?? '',
    status: item.status,
  };
}

/** Key pattern (create only) + length bounds. The container gates submit on this. */
export function isUpsertFormValid(values: IqServiceUpsertValues, mode: 'create' | 'edit'): boolean {
  const keyValid = mode === 'edit' || IQ_SERVICE_KEY_PATTERN.test(values.iqServiceKey);
  const lengthsValid =
    values.displayName.length <= MAX_DISPLAY_NAME &&
    values.description.length <= MAX_DESCRIPTION &&
    values.internetqEmailRef.length <= MAX_EMAIL_REF;
  return keyValid && lengthsValid;
}

interface IqServiceInventoryFieldsProps {
  values: IqServiceUpsertValues;
  onChange: (patch: Partial<IqServiceUpsertValues>) => void;
  mode: 'create' | 'edit';
  /** Original status of the edited record — drives the legal status transitions. */
  initialStatus?: IqServiceInventoryStatus;
}

/**
 * Presentation-only field set for register (UC-2) and edit (UC-3), shared by the
 * per-merchant dialog and the platform-overview drawer (Spec 356). Fully controlled:
 * the container owns the form state, prefill and submit; this component renders the
 * inputs and the inline key-pattern error. The `null`↔`__none__`-sentinel mapping for
 * the optional Flow/Booking selects lives here so callers work with a clean
 * `IqServiceUpsertValues` (nulls), not the Select sentinel.
 */
export function IqServiceInventoryFields({
  values,
  onChange,
  mode,
  initialStatus,
}: IqServiceInventoryFieldsProps) {
  const { t } = useTranslation('iq-service-inventory');

  const keyValid = mode === 'edit' || IQ_SERVICE_KEY_PATTERN.test(values.iqServiceKey);
  const statusOptions =
    mode === 'edit'
      ? iqServiceEditStatusOptions(initialStatus ?? values.status)
      : IQ_SERVICE_CREATE_STATUS_OPTIONS;

  return (
    <>
      <div className="grid gap-1.5">
        <Label htmlFor="iqinv-key">{t('upsert.fields.iqServiceKey')}</Label>
        <Input
          id="iqinv-key"
          value={values.iqServiceKey}
          onChange={(e) => onChange({ iqServiceKey: e.target.value })}
          disabled={mode === 'edit'}
          maxLength={128}
          className="font-mono"
          aria-invalid={!keyValid}
        />
        {!keyValid && <p className="text-xs text-destructive">{t('upsert.error.keyPattern')}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="iqinv-flow">{t('upsert.fields.flowType')}</Label>
          <Select
            value={values.flowType ?? NONE}
            onValueChange={(v) => onChange({ flowType: v === NONE ? null : (v as IqServiceFlowType) })}
          >
            <SelectTrigger id="iqinv-flow">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t('upsert.fields.none')}</SelectItem>
              <SelectItem value="WAP_CLICK">{t('flowType.WAP_CLICK')}</SelectItem>
              <SelectItem value="WEB_TAN">{t('flowType.WEB_TAN')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="iqinv-booking">{t('upsert.fields.bookingType')}</Label>
          <Select
            value={values.bookingType ?? NONE}
            onValueChange={(v) =>
              onChange({ bookingType: v === NONE ? null : (v as IqServiceBookingType) })
            }
          >
            <SelectTrigger id="iqinv-booking">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t('upsert.fields.none')}</SelectItem>
              <SelectItem value="SUBSCRIPTION">{t('bookingType.SUBSCRIPTION')}</SelectItem>
              <SelectItem value="OTP">{t('bookingType.OTP')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="iqinv-displayName">{t('upsert.fields.displayName')}</Label>
        <Input
          id="iqinv-displayName"
          value={values.displayName}
          onChange={(e) => onChange({ displayName: e.target.value })}
          maxLength={MAX_DISPLAY_NAME}
        />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="iqinv-description">{t('upsert.fields.description')}</Label>
        <Textarea
          id="iqinv-description"
          value={values.description}
          onChange={(e) => onChange({ description: e.target.value })}
          maxLength={MAX_DESCRIPTION}
          rows={3}
        />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="iqinv-emailRef">{t('upsert.fields.internetqEmailRef')}</Label>
        <Input
          id="iqinv-emailRef"
          value={values.internetqEmailRef}
          onChange={(e) => onChange({ internetqEmailRef: e.target.value })}
          maxLength={MAX_EMAIL_REF}
        />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="iqinv-status">{t('upsert.fields.status')}</Label>
        <Select
          value={values.status}
          onValueChange={(v) => onChange({ status: v as IqServiceInventoryStatus })}
        >
          <SelectTrigger id="iqinv-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`status.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}
